import { createContext, useContext, type ReactNode } from "react";
import { QueryClient } from "@tanstack/react-query";
import { cookieAuth, createClient, createWsClient, tokenAuth, type ApiClient, type WsClient } from "@agentsaas/client-sdk";
import { ApiError } from "@agentsaas/protocol";
import { wsUrlFor, type AppConfig } from "./config";
import type { MockBackend } from "../mock/backend";
import { nativeBridge, type NativeBridge } from "./native";

/** 应用级服务：REST 客户端、WS 客户端、查询缓存；mock 模式下附带 MockBackend */
export interface Services {
  config: AppConfig;
  api: ApiClient;
  ws: WsClient;
  queryClient: QueryClient;
  /** 对象存储（预签名地址）的上传 / 下载；mock 模式下由 MockBackend 接住 */
  storageFetch: typeof fetch;
  mock?: MockBackend;
  /** 原生壳的桥（浏览器里为 undefined） */
  native?: NativeBridge;
}

export interface ServiceHooks {
  getLocale(): string;
  onUnauthenticated(): void;
}

export async function createServices(config: AppConfig, hooks: ServiceHooks): Promise<Services> {
  let mock: MockBackend | undefined;
  let fetchImpl: typeof fetch | undefined;
  if (config.mock) {
    const { MockBackend } = await import("../mock/backend");
    mock = new MockBackend({ latencyMs: 60 });
    fetchImpl = mock.fetch(config.apiBase);
  }
  const native = nativeBridge();
  // 原生壳：先等桥把安全存储里的 refresh token 读进内存（可能弹生物识别），再用 bearer 模式
  if (native) await native.ready.catch(() => undefined);
  const api = createClient({
    baseUrl: config.apiBase,
    fetch: fetchImpl,
    auth: native ? tokenAuth(native.auth.tokenStore, { onUnauthenticated: hooks.onUnauthenticated }) : cookieAuth({ onUnauthenticated: hooks.onUnauthenticated }),
    headers: () => ({ "Accept-Language": hooks.getLocale() }),
  });
  const ws = createWsClient({
    url: async () => {
      const { ticket } = await api.call("wsTicket");
      return `${wsUrlFor(config.apiBase)}?ticket=${encodeURIComponent(ticket)}`;
    },
    createSocket: mock?.hub.createSocket,
  });
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (n, err) => n < 2 && !(err instanceof ApiError && err.status < 500),
        refetchOnWindowFocus: false,
      },
    },
  });
  const storageFetch: typeof fetch = mock ? mock.storageFetch : (input, init) => fetch(input, init);
  return { config, api, ws, queryClient, storageFetch, mock, native };
}

const Ctx = createContext<Services | null>(null);

export function ServicesProvider({ services, children }: { services: Services; children: ReactNode }) {
  return <Ctx.Provider value={services}>{children}</Ctx.Provider>;
}

export function useServices(): Services {
  const s = useContext(Ctx);
  if (!s) throw new Error("ServicesProvider missing");
  return s;
}
